/**
 * GitHub Branch Visualization Overlay
 * Displays GitHub branches as an overlay in graphical mode
 * Shows branch structure, karma flow, and active branches
 */

import React, { useState, useEffect, useRef } from 'react';
import { useGameStore } from '@/state/gameStore';
import { gitGameStateService } from '@/services/gitGameStateService';
import { motion, AnimatePresence } from 'framer-motion';

interface BranchNode {
  id: string;
  name: string;
  karma: number;
  commits: number;
  player: string;
  isActive: boolean;
  isMainBranch: boolean;
  children?: BranchNode[];
}

interface GitHubBranchOverlayProps {
  isVisible: boolean;
  onClose: () => void;
}

export const GitHubBranchOverlay: React.FC<GitHubBranchOverlayProps> = ({
  isVisible,
  onClose,
}) => {
  const cartridgeId = useGameStore((state) => state.selectedCartridge?.id);
  const userId = useGameStore((state) => state.userProfile?.uid);
  const currentBranchId = useGameStore((state) => state.currentBranchId);

  const [branches, setBranches] = useState<BranchNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [hoveredBranch, setHoveredBranch] = useState<string | null>(null);

  useEffect(() => {
    if (isVisible && cartridgeId) {
      loadBranchData();
    }
  }, [isVisible, cartridgeId]);

  const loadBranchData = async () => {
    if (!cartridgeId) return;

    try {
      setLoading(true);
      const branchTree = await gitGameStateService.getBranchTree(cartridgeId);
      
      // Transform tree data to flat list for overlay
      const branchList: BranchNode[] = [];
      const traverse = (node: any) => {
        if (node.name && node.name !== 'Main Branch') {
          branchList.push({
            id: node.name,
            name: node.name,
            karma: node.karma || 0,
            commits: node.commits || 0,
            player: node.player || 'Unknown',
            isActive: node.name === currentBranchId,
            isMainBranch: false,
          });
        }
        if (node.children) {
          node.children.forEach(traverse);
        }
      };
      traverse(branchTree);

      setBranches(branchList);
    } catch (error) {
      console.error('[GitHubBranchOverlay] Error loading branch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getKarmaColor = (karma: number): string => {
    if (karma > 50) return 'text-blue-400 border-blue-400';
    if (karma > 20) return 'text-green-400 border-green-400';
    if (karma < -50) return 'text-red-600 border-red-600';
    if (karma < -20) return 'text-orange-600 border-orange-600';
    return 'text-gray-400 border-gray-400';
  };

  const getKarmaIcon = (karma: number): string => {
    if (karma > 50) return '☀';
    if (karma > 20) return '✦';
    if (karma < -50) return '☠';
    if (karma < -20) return '⚡';
    return '☯';
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-4xl h-[80vh] bg-slate-900 border border-cyan-500/40 rounded-xl p-6 shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-cyan-300">GitHub Branch Visualization</h2>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors"
            >
              ✕ Close
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-400">Loading branch data...</p>
              </div>
            </div>
          ) : branches.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-slate-400">No branches found</p>
            </div>
          ) : (
            <div className="h-full overflow-y-auto">
              {/* Branch List */}
              <div className="space-y-3">
                {branches.map((branch) => (
                  <motion.div
                    key={branch.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      branch.isActive
                        ? 'bg-cyan-500/20 border-cyan-400'
                        : hoveredBranch === branch.id
                        ? 'bg-slate-800 border-slate-600'
                        : 'bg-slate-800/50 border-slate-700'
                    } ${getKarmaColor(branch.karma)}`}
                    onMouseEnter={() => setHoveredBranch(branch.id)}
                    onMouseLeave={() => setHoveredBranch(null)}
                    onClick={() => setSelectedBranch(selectedBranch === branch.id ? null : branch.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getKarmaIcon(branch.karma)}</span>
                        <div>
                          <h3 className="font-bold text-lg">{branch.name}</h3>
                          <p className="text-sm text-slate-400">
                            {branch.commits} commits • {branch.player}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono">
                          Karma: {branch.karma > 0 ? '+' : ''}{branch.karma}
                        </div>
                        {branch.isActive && (
                          <div className="text-xs text-cyan-400 mt-1">● Active</div>
                        )}
                      </div>
                    </div>

                    {/* Branch Details (when selected) */}
                    {selectedBranch === branch.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mt-4 pt-4 border-t border-slate-700"
                      >
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-slate-400">Branch ID:</span>
                            <div className="font-mono text-slate-200">{branch.id}</div>
                          </div>
                          <div>
                            <span className="text-slate-400">Karma Status:</span>
                            <div className={getKarmaColor(branch.karma)}>
                              {branch.karma > 0 ? 'Positive' : branch.karma < 0 ? 'Negative' : 'Neutral'}
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-400">Commits:</span>
                            <div className="text-slate-200">{branch.commits}</div>
                          </div>
                          <div>
                            <span className="text-slate-400">Created By:</span>
                            <div className="text-slate-200">{branch.player}</div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-slate-700 text-xs text-slate-400 text-center">
            {branches.length} branch{branches.length !== 1 ? 'es' : ''} loaded
            {currentBranchId && ` • Current: ${currentBranchId}`}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

